import { useMemo, useState } from "react";
import type { InstanceDetail } from "@keydials/api-client";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";
import { fmtDate } from "../lib/ui";
import { bucketOf, comingUpCounts, comingUpRows, daysPhrase, type ComingUpBucket, type ComingUpRow } from "./comingUpModel";

const HORIZONS = [7, 30, 90] as const;

const BUCKETS: { id: ComingUpBucket; label: string; tone: string }[] = [
  { id: "overdue", label: "Overdue", tone: "var(--st-overdue, #b91c1c)" },
  { id: "today", label: "Today", tone: "var(--st-amber, #b45309)" },
  { id: "week", label: "Next 7 days", tone: "var(--text)" },
  { id: "later", label: "Later", tone: "var(--text-muted)" },
];

/**
 * "Coming up" (KEY-COMINGUP): the one screen a busy person opens most — everything on the dial with a
 * date, soonest first, overdue at the top, with who it is for and what it is showing. Built from the
 * detail alone (see comingUpModel), so it costs no request and can never disagree with the dial.
 */
export function ComingUpPanel({ detail, noun = DEFAULT_SEGMENT_NOUN, onOpen }: {
  detail: InstanceDetail;
  noun?: SegmentNoun;
  /** Opens the action point a row lives in. */
  onOpen: (sectionKey: string, segmentKey: string) => void;
}) {
  const [horizon, setHorizon] = useState<(typeof HORIZONS)[number]>(30);
  const rows = useMemo(() => comingUpRows(detail, horizon), [detail, horizon]);
  const grouped = useMemo(() => {
    const by = new Map<ComingUpBucket, ComingUpRow[]>();
    for (const row of rows) {
      const b = bucketOf(row);
      by.set(b, [...(by.get(b) ?? []), row]);
    }
    return by;
  }, [rows]);

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Everything with a date, soonest first. Overdue is always shown.
        </div>
        <div role="group" aria-label="How far ahead" style={{ display: "inline-flex", gap: 4 }}>
          {HORIZONS.map((h) => (
            <button
              key={h}
              type="button"
              className="kd-focusable"
              aria-pressed={horizon === h}
              onClick={() => setHorizon(h)}
              style={{
                fontSize: 12, padding: "4px 10px", borderRadius: 999, cursor: "pointer",
                border: "1px solid var(--border)",
                background: horizon === h ? "var(--text)" : "transparent",
                color: horizon === h ? "var(--bg, #fff)" : "var(--text-muted)",
              }}
            >
              {h} days
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 14, color: "var(--text-faint)" }}>
          Nothing dated in the next {horizon} days. Set a date on an item or {noun.one} and it appears here.
        </p>
      ) : BUCKETS.filter((b) => grouped.has(b.id)).map((b) => (
        <section key={b.id} aria-label={b.label}>
          <h4 style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: b.tone }}>
            {b.label} · {grouped.get(b.id)!.length}
          </h4>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
            {grouped.get(b.id)!.map((row) => (
              <li key={row.key} style={{ borderTop: "var(--border-w) solid var(--border)" }}>
                <button
                  type="button"
                  className="kd-row-hover kd-focusable"
                  onClick={() => onOpen(row.sectionKey, row.segmentKey)}
                  style={{ width: "100%", textAlign: "left", background: "none", border: 0, padding: "9px 4px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", color: "inherit" }}
                >
                  <span
                    aria-hidden
                    style={{ width: 10, height: 10, borderRadius: 999, flex: "none", background: row.status?.colour ?? "var(--border)" }}
                  />
                  <span style={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 14, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.title}
                      {row.people.length > 0 && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-muted)" }}>for {row.people.join(", ")}</span>
                      )}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.where}{row.axisLabel ? ` · ${row.axisLabel}` : ""}{row.status ? ` · ${row.status.name}` : ""}
                    </span>
                  </span>
                  <span style={{ flex: "none", textAlign: "right", display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 13, color: "var(--text)" }}>{fmtDate(row.reviewDate)}</span>
                    <span style={{ fontSize: 12, color: b.tone }}>{daysPhrase(row.daysUntil)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * The one line under the ring on the Overview (KEY-COMINGUP): "2 overdue · 1 today · 4 this week", tapping
 * through to the Coming up tab. Nothing at all when nothing is dated in the next week — a row of zeros would
 * only teach people to stop reading it.
 */
export function ComingUpStrip({ detail, onOpen }: { detail: InstanceDetail; onOpen: () => void }) {
  const counts = useMemo(() => comingUpCounts(detail), [detail]);
  if (counts.empty) return null;
  const parts: { n: number; label: string; tone: string }[] = [
    { n: counts.overdue, label: "overdue", tone: "var(--st-overdue, #b91c1c)" },
    { n: counts.today, label: "today", tone: "var(--st-amber, #b45309)" },
    { n: counts.week, label: "this week", tone: "var(--text)" },
  ].filter((p) => p.n > 0);
  return (
    <button
      type="button"
      className="kd-focusable kd-row-hover"
      onClick={onOpen}
      aria-label={`Coming up: ${parts.map((p) => `${p.n} ${p.label}`).join(", ")}. Open the list.`}
      style={{
        marginTop: 14, display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "8px 12px", borderRadius: 999, border: "1px solid var(--border)", background: "transparent",
        cursor: "pointer", color: "var(--text)", fontSize: 13,
      }}
    >
      {parts.map((p, i) => (
        <span key={p.label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {i > 0 && <span aria-hidden style={{ color: "var(--text-faint)" }}>·</span>}
          <strong style={{ color: p.tone, fontWeight: 700 }}>{p.n}</strong>
          <span style={{ color: "var(--text-muted)" }}>{p.label}</span>
        </span>
      ))}
      <span aria-hidden style={{ color: "var(--text-faint)" }}>›</span>
    </button>
  );
}
